import React, {
  SelectHTMLAttributes,
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { cn } from 'utils/helpers';
import { focusWithoutScroll, preserveScrollWhile } from 'utils/focusWithoutScroll';
import { SelectOption } from 'types/index';
import { useLicenseGate } from './LicenseGate';

interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string;
  helperText?: string;
  options: SelectOption[];
  placeholder?: string;
  fullWidth?: boolean;
  disableOnLicenseExpired?: boolean;
  searchable?: boolean;
  searchPlaceholder?: string;
}

function mergeRefs<T>(
  ...refs: Array<React.Ref<T> | undefined>
): React.RefCallback<T> {
  return (value) => {
    refs.forEach((ref) => {
      if (typeof ref === 'function') {
        ref(value);
      } else if (ref && typeof ref === 'object') {
        (ref as React.MutableRefObject<T | null>).current = value;
      }
    });
  };
}

function filterOptions(options: SelectOption[], query: string): SelectOption[] {
  const normalized = query.trim().toLowerCase();
  if (!normalized) return options;

  return options.filter((option) =>
    option.label.toLowerCase().includes(normalized)
  );
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      label,
      error,
      helperText,
      options,
      placeholder = 'Select an option',
      fullWidth = false,
      className,
      id,
      disabled,
      disableOnLicenseExpired = true,
      searchable,
      searchPlaceholder = 'Search...',
      value,
      onChange,
      onBlur,
      name,
      required,
      ...props
    },
    ref
  ) => {
    const { shouldDisable } = useLicenseGate();
    const generatedId = useId();
    const selectId = id || generatedId;
    const isDisabled = disabled || (disableOnLicenseExpired && shouldDisable);
    const isSearchable = searchable ?? true;

    const containerRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const hiddenSelectRef = useRef<HTMLSelectElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [highlightedIndex, setHighlightedIndex] = useState(0);

    const selectedOption = useMemo(
      () => options.find((option) => String(option.value) === String(value)),
      [options, value]
    );

    const filteredOptions = useMemo(
      () => filterOptions(options, searchQuery),
      [options, searchQuery]
    );

    const closeDropdown = useCallback(() => {
      setIsOpen(false);
      setSearchQuery('');
      setHighlightedIndex(0);
    }, []);

    useEffect(() => {
      if (!isOpen) return;

      setHighlightedIndex(0);
      preserveScrollWhile(() => {
        focusWithoutScroll(searchInputRef.current);
      });
    }, [isOpen]);

    useEffect(() => {
      if (!isOpen) return;

      const handlePointerDown = (event: MouseEvent | TouchEvent) => {
        const target = event.target as Node;
        if (containerRef.current?.contains(target)) return;
        if (dropdownRef.current?.contains(target)) return;

        closeDropdown();
        onBlur?.({
          target: hiddenSelectRef.current,
          currentTarget: hiddenSelectRef.current,
        } as React.FocusEvent<HTMLSelectElement>);
      };

      document.addEventListener('mousedown', handlePointerDown);
      document.addEventListener('touchstart', handlePointerDown);

      return () => {
        document.removeEventListener('mousedown', handlePointerDown);
        document.removeEventListener('touchstart', handlePointerDown);
      };
    }, [isOpen, closeDropdown, onBlur]);

    useEffect(() => {
      if (highlightedIndex >= filteredOptions.length) {
        setHighlightedIndex(Math.max(filteredOptions.length - 1, 0));
      }
    }, [filteredOptions.length, highlightedIndex]);

    const emitChange = (nextValue: string | number) => {
      if (hiddenSelectRef.current) {
        hiddenSelectRef.current.value = String(nextValue);
      }

      onChange?.({
        target: {
          value: String(nextValue),
          name: name ?? hiddenSelectRef.current?.name ?? '',
        },
        currentTarget: hiddenSelectRef.current!,
      } as React.ChangeEvent<HTMLSelectElement>);
    };

    const handleSelectOption = (option: SelectOption) => {
      if (option.disabled) return;
      emitChange(option.value);
      closeDropdown();
      requestAnimationFrame(() => focusWithoutScroll(triggerRef.current));
    };

    const handleTriggerMouseDown = (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      if (isDisabled) return;
      preserveScrollWhile(() => setIsOpen((open) => !open));
    };

    const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setHighlightedIndex((index) =>
          Math.min(index + 1, Math.max(filteredOptions.length - 1, 0))
        );
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        setHighlightedIndex((index) => Math.max(index - 1, 0));
      } else if (event.key === 'Enter') {
        event.preventDefault();
        const option = filteredOptions[highlightedIndex];
        if (option) handleSelectOption(option);
      } else if (event.key === 'Escape') {
        event.preventDefault();
        closeDropdown();
        requestAnimationFrame(() => focusWithoutScroll(triggerRef.current));
        onBlur?.({
          target: hiddenSelectRef.current,
          currentTarget: hiddenSelectRef.current,
        } as React.FocusEvent<HTMLSelectElement>);
      }
    };

    const dropdownPanel = isOpen ? (
      <div
        ref={dropdownRef}
        className="absolute left-0 right-0 top-full z-50 mt-1.5 min-w-[16rem] rounded-xl border border-secondary-200/80 bg-white shadow-elevated overflow-hidden"
      >
        <div className="border-b border-secondary-100 p-2">
          <input
            ref={searchInputRef}
            type="text"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            onKeyDown={handleSearchKeyDown}
            placeholder={searchPlaceholder}
            className="w-full rounded-md border border-secondary-300 px-3 py-2 text-sm text-secondary-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
        <ul
          role="listbox"
          aria-labelledby={label ? `${selectId}-label` : undefined}
          className="max-h-60 overflow-y-auto py-1"
        >
          {filteredOptions.length === 0 ? (
            <li className="px-3 py-2 text-sm text-secondary-500">
              No matches found
            </li>
          ) : (
            filteredOptions.map((option, index) => {
              const isSelected = String(option.value) === String(value);
              const isHighlighted = index === highlightedIndex;

              return (
                <li key={option.value} role="presentation">
                  <button
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    disabled={option.disabled}
                    onMouseEnter={() => setHighlightedIndex(index)}
                    onClick={() => handleSelectOption(option)}
                    className={cn(
                      'w-full px-3 py-2 text-left text-sm transition-colors',
                      option.disabled
                        ? 'cursor-not-allowed text-secondary-400'
                        : 'cursor-pointer text-secondary-900',
                      isHighlighted && !option.disabled && 'bg-primary-50',
                      isSelected && 'font-medium text-primary-700'
                    )}
                  >
                    {option.label}
                  </button>
                </li>
              );
            })
          )}
        </ul>
      </div>
    ) : null;

    if (isSearchable) {
      return (
        <div
          className={cn('flex flex-col gap-1.5', fullWidth && 'w-full')}
          ref={containerRef}
        >
          {label && (
            <label
              id={`${selectId}-label`}
              htmlFor={`${selectId}-trigger`}
              className="text-xs font-medium text-secondary-600"
            >
              {label}
              {required && <span className="text-red-500 ml-1">*</span>}
            </label>
          )}

          <div className="relative">
            <select
              ref={mergeRefs(ref, hiddenSelectRef)}
              id={selectId}
              name={name}
              value={value ?? ''}
              required={required}
              disabled={isDisabled}
              className="sr-only"
              tabIndex={-1}
              aria-hidden="true"
              onChange={onChange}
              onBlur={onBlur}
              {...props}
            >
              <option value="" disabled>
                {placeholder}
              </option>
              {options.map((option) => (
                <option
                  key={option.value}
                  value={option.value}
                  disabled={option.disabled}
                >
                  {option.label}
                </option>
              ))}
            </select>

            <button
              ref={triggerRef}
              type="button"
              id={`${selectId}-trigger`}
              aria-haspopup="listbox"
              aria-expanded={isOpen}
              aria-labelledby={label ? `${selectId}-label` : undefined}
              disabled={isDisabled}
              onMouseDown={handleTriggerMouseDown}
              className={cn(
                'w-full px-3 sm:px-4 py-2.5 sm:py-2 pr-9 sm:pr-10 rounded-xl border bg-white transition-colors duration-200 text-left',
                'text-base sm:text-sm text-secondary-900',
                'focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-400',
                'disabled:bg-secondary-50 disabled:cursor-not-allowed',
                'min-h-[44px] sm:min-h-[40px]',
                error
                  ? 'border-rose-300 focus:ring-rose-500/15 focus:border-rose-400'
                  : 'border-secondary-200 hover:border-secondary-300',
                !selectedOption && 'text-secondary-400',
                className
              )}
            >
              <span className="block truncate">
                {selectedOption?.label ?? placeholder}
              </span>
            </button>

            <div className="absolute right-2.5 sm:right-3 top-1/2 -translate-y-1/2 pointer-events-none text-secondary-400">
              <svg
                className={cn(
                  'w-5 h-5 sm:w-4 sm:h-4 transition-transform',
                  isOpen && 'rotate-180'
                )}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </div>

            {dropdownPanel}
          </div>

          {(error || helperText) && (
            <p
              className={cn(
                'text-sm',
                error ? 'text-red-500' : 'text-secondary-500'
              )}
            >
              {error || helperText}
            </p>
          )}
        </div>
      );
    }

    return (
      <div className={cn('flex flex-col gap-1.5', fullWidth && 'w-full')}>
        {label && (
          <label
            htmlFor={selectId}
            className="text-xs font-medium text-secondary-600"
          >
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}

        <div className="relative">
          <select
            ref={ref}
            id={selectId}
            name={name}
            value={value}
            required={required}
            disabled={isDisabled}
            onChange={onChange}
            onBlur={onBlur}
            className={cn(
              'w-full px-3 sm:px-4 py-2.5 sm:py-2 pr-9 sm:pr-10 rounded-xl border bg-white transition-colors duration-200',
              'text-base sm:text-sm text-secondary-900',
              'focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-400',
              'disabled:bg-secondary-50 disabled:cursor-not-allowed',
              'appearance-none cursor-pointer',
              'min-h-[44px] sm:min-h-[40px]',
              error
                ? 'border-rose-300 focus:ring-rose-500/15 focus:border-rose-400'
                : 'border-secondary-200 hover:border-secondary-300',
              className
            )}
            {...props}
          >
            <option value="" disabled>
              {placeholder}
            </option>
            {options.map((option) => (
              <option
                key={option.value}
                value={option.value}
                disabled={option.disabled}
              >
                {option.label}
              </option>
            ))}
          </select>

          <div className="absolute right-2.5 sm:right-3 top-1/2 -translate-y-1/2 pointer-events-none text-secondary-400">
            <svg
              className="w-5 h-5 sm:w-4 sm:h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
        </div>

        {(error || helperText) && (
          <p
            className={cn(
              'text-sm',
              error ? 'text-red-500' : 'text-secondary-500'
            )}
          >
            {error || helperText}
          </p>
        )}
      </div>
    );
  }
);

Select.displayName = 'Select';
